import React, { useState } from 'react';
import Select from 'react-select';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid2';
import { Typography } from '@mui/material';
import { getInputProps } from 'pages/DashboardHelpers';
import { useDispatch } from 'react-redux';
import { createSubSectionOrChoice } from 'actions/userActions';

const formControlStyles = {
    display: 'flex',
    width: '100%',
    marginBottom: '14px',
};

const formFieldStyles = {
    control: (provided) => ({ ...provided, padding: '6px', borderRadius: '12px' }),
};

const toSelectOptions = (items = [], key) =>
    items.map((item) => ({ value: item[key], label: item[key] }));

const FormField = ({
    label,
    value,
    onChange,
    options,
    placeholder,
    isSelect = false,
    isDisabled = false,
    type = 'text',
}) => (
    <Grid container spacing={2} alignItems="center" sx={formControlStyles}>
        <Grid sx={{ width: '200px', flexShrink: 0 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>{label}</Typography>
        </Grid>
        <Grid sx={{ flex: 1 }}>
            {isSelect ? (
                <FormControl fullWidth>
                    <Select
                        styles={formFieldStyles}
                        placeholder={placeholder}
                        value={value}
                        options={options}
                        onChange={onChange}
                        menuPosition="fixed"
                        isClearable={false}
                        isDisabled={isDisabled}
                    />
                </FormControl>
            ) : (
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    type={type}
                    slotProps={{ input: getInputProps() }}
                    disabled={isDisabled}
                />
            )}
        </Grid>
    </Grid>
);

const initialFormData = {
    productLine: null,
    tabCategory: null,
    verticalStepperHeader: '',
    verticalStepperHeaderForChoice: null,
    subSectionOrder: null,
    choiceId: '',
    choiceName: '',
    choiceOrder: null,
};


const AddChoiceOrSubSectionDialog = ({ open, onClose, ocUiSpecNested, email, dialogType }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(initialFormData);

    const isSubSection = dialogType === 'subSection';
    const { productLine, tabCategory, verticalStepperHeader, verticalStepperHeaderForChoice, subSectionOrder, choiceId, choiceName, choiceOrder } = formData;

    const selectedProductLine = productLine
        ? ocUiSpecNested.find((cat) => cat.productLine === productLine.value)
        : null;
    const selectedTabCategory = tabCategory
        ? selectedProductLine?.tabCategories?.find((tab) => tab.tabCategory === tabCategory.value)
        : null;

    const categoryList = toSelectOptions(ocUiSpecNested, 'productLine');
    const tabCategoryList = toSelectOptions(selectedProductLine?.tabCategories, 'tabCategory');
    const verticalStepperHeaderList = toSelectOptions(selectedTabCategory?.subSections, 'verticalStepperHeader');
    const choiceList = selectedTabCategory?.subSections?.flatMap((subSection) => subSection.choices) || [];
    const choiceNameList = toSelectOptions(choiceList, 'choiceName');

    const handleTextChange = (fieldName) => (event) => {
        const value = event.target.value;
        const finalValue = fieldName === 'choiceId' ? value.toUpperCase() : value;
        setFormData((prev) => ({ ...prev, [fieldName]: finalValue }));
    };

    const handleSelectChange = (fieldName) => (selected) => {
        setFormData((prev) => {
            const newData = { ...prev, [fieldName]: selected };
            
            if (fieldName === 'productLine') {
                newData.tabCategory = null;
                newData.verticalStepperHeaderForChoice = null;
                newData.subSectionOrder = null;
                newData.choiceOrder = null;
            } else if (fieldName === 'tabCategory') {
                newData.verticalStepperHeaderForChoice = null;
                newData.subSectionOrder = null;
            }
            
            return newData;
        });
    };

    const handleClose = () => {
        setFormData(initialFormData);
        onClose();
    };

    const handleSubmit = () => {
        const payload = isSubSection
            ? {
                productLine: productLine?.value,
                tabCategory: tabCategory?.value,
                subSectionOrderNextTo: subSectionOrder?.value,
                verticalStepperHeader,
                createdBy: email,
            }
            : {
                productLine: productLine?.value,
                tabCategory: tabCategory?.value,
                verticalStepperHeader: verticalStepperHeaderForChoice?.value,
                choiceId,
                choiceName,
                choiceOrderNextTo: choiceOrder?.value,
                createdBy: email,
            };
        dispatch(createSubSectionOrChoice(payload, isSubSection));
        handleClose();
    };

    const isAddDisabled = isSubSection
        ? !productLine || !tabCategory || !verticalStepperHeader || !subSectionOrder
        : !productLine || !tabCategory || !verticalStepperHeaderForChoice || !choiceId || !choiceName || !choiceOrder;

    const commonFields = [
        {
            label: 'Product Line',
            fieldName: 'productLine',
            value: productLine,
            isSelect: true,
            options: categoryList,
            placeholder: 'Select Product Line',
        },
        {
            label: 'Tab Category',
            fieldName: 'tabCategory',
            value: tabCategory,
            isSelect: true,
            isDisabled: !productLine,
            options: tabCategoryList,
            placeholder: 'Select Tab Category',
        },
    ];

    const subSectionFields = [
        {
            label: 'Vertical Stepper Header',
            fieldName: 'verticalStepperHeader',
            value: verticalStepperHeader,
            isSelect: false,
            isDisabled: !tabCategory,
            placeholder: 'Enter Vertical Stepper Header',
        },
        {
            label: 'Sub Section Order Next To',
            fieldName: 'subSectionOrder',
            value: subSectionOrder,
            isSelect: true,
            isDisabled: !tabCategory,
            options: verticalStepperHeaderList,
            placeholder: 'Insert After Sub Section Name',
        },
    ];

    const choiceFields = [
        {
            label: 'Vertical Stepper Header',
            fieldName: 'verticalStepperHeaderForChoice',
            value: verticalStepperHeaderForChoice,
            isSelect: true,
            isDisabled: !tabCategory,
            options: verticalStepperHeaderList,
            placeholder: 'Select Vertical Stepper Header',
        },
        {
            label: 'Choice ID',
            fieldName: 'choiceId',
            value: choiceId,
            isSelect: false,
            isDisabled: !verticalStepperHeaderForChoice,
            placeholder: 'Enter Choice ID',
        },
        {
            label: 'Choice Name',
            fieldName: 'choiceName',
            value: choiceName,
            isSelect: false,
            isDisabled: !verticalStepperHeaderForChoice,
            placeholder: 'Enter Choice Name',
        },
        {
            label: 'Choice Order Next To',
            fieldName: 'choiceOrder',
            value: choiceOrder,
            isSelect: true,
            isDisabled: !verticalStepperHeaderForChoice,
            options: choiceNameList,
            placeholder: 'Insert After Choice Name',
        },
    ];

    const fieldsToRender = [...commonFields, ...(isSubSection ? subSectionFields : choiceFields)];

    return (
        <Dialog open={open} onClose={handleClose} className="custom-dialog" sx={{ '& .MuiDialog-paper': { minWidth: '600px' } }}>
            <DialogTitle variant="h6">{isSubSection ? 'Add New Sub Section' : 'Add New Choice'}</DialogTitle>
            <DialogContent>
                {fieldsToRender.map((field) => (
                    <FormField
                        key={field.fieldName}
                        label={field.label}
                        value={field.value}
                        onChange={field.isSelect ? handleSelectChange(field.fieldName) : handleTextChange(field.fieldName)}
                        options={field.options}
                        placeholder={field.placeholder}
                        isSelect={field.isSelect}
                        isDisabled={field.isDisabled}
                    />
                ))}
            </DialogContent>
            <DialogActions className="dialog-actions">
                <Button variant="outlined" className="cancel-button" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    className="add-Button"
                    disabled={isAddDisabled}
                >
                    +&nbsp; Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddChoiceOrSubSectionDialog;
